import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';

import { CollectionType } from '@/enums/collection-type';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { calculateUrl } from '@/helpers/state-helper';

import { NewPageType } from '@/@types/graphql-types';
import {
	GetUpcomingProvidersDocument,
	GetUpcomingProvidersQueryVariables,
	GetUpcomingProvidersQuery,
} from '@/pages/graphql/queries/GetUpcomingProviders.query';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';
import { useQuery } from '@/helpers/composables/useApollo';

import { PackagesSelectionModal } from '@/lazy-load-components';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ProviderIconSquare from '@/features/ui/components/ProviderIconSquare.vue';
import { useRouter } from 'vue-router/composables';
import { WebLocale } from '@/enums/web-locale';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import Provider from '@/interfaces/provider';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

interface FilterBarProviderSeoProps {
	collectionType: CollectionType;
	currentFilters: FilterCollection;
	webLocale: WebLocale;
	addButtonVisible?: boolean;
	showAddButtonFirst?: boolean;
	userProviders: Provider[];
	newPageType?: NewPageType;
	providersClickable?: boolean;
	updateRouteState?: boolean;
	trackEvent?: boolean;
	userSelectedPackages?: string[];
	selectionType?: 'single' | 'bundle';
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FilterBarProviderSeo',
  props: {
    collectionType: null,
    currentFilters: null,
    webLocale: null,
    addButtonVisible: { type: Boolean, default: true },
    showAddButtonFirst: { type: Boolean, default: false },
    userProviders: null,
    newPageType: null,
    providersClickable: { type: Boolean, default: true },
    updateRouteState: { type: Boolean, default: true },
    trackEvent: { type: Boolean, default: true },
    userSelectedPackages: null,
    selectionType: { default: 'single' }
  },
  emits: ["toggle"],
  setup(__props: any, { emit }: { emit: ({
	(
		event: 'toggle',
		payload: {
			filterName: keyof FilterCollection;
			value: any;
			selectedState: 1 | 0;
			updateRouteState?: boolean;
			userSelectedPackages?: string[];
			trackEvent?: boolean;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { collectionType: CollectionType, currentFilters: FilterCollection, webLocale: WebLocale, addButtonVisible: boolean, showAddButtonFirst: boolean, userProviders: Provider[], newPageType?: NewPageType, providersClickable: boolean, updateRouteState: boolean, trackEvent: boolean, userSelectedPackages?: string[], selectionType: 'single' | 'bundle' };

const FilterBarProviderMulti = () => import('@/components/filter-bar/seo/FilterBarProviderMulti.vue');





const router = useRouter();

const constantStore = useConstantStore();
const filterStore = useFilterStore();
const languageStore = useLanguageStore();
const routingStore = useRoutingStore();
const userStore = useUserStore();

const IMG_PROVIDERS_BAR_LOAD_COUNT = globalThis.IMG_PROVIDERS_BAR_LOAD_COUNT ?? 8;
const plusIcon = `/${ASSETS_DIR}/img/plus_icon.png`;

const isModalOpening = ref(false);
const providerUrls = ref<Record<string, string>>({});

const enabled = computed(() =>
	['app.titles.upcoming', 'app.titles.new', 'app.titles.pricedrops'].some(routeName =>
		routingStore.activeRoute?.name?.startsWith(routeName)
	)
);

const { result, onResult } = useQuery<GetUpcomingProvidersQuery, GetUpcomingProvidersQueryVariables>(
	GetUpcomingProvidersDocument,
	() => ({
		country: languageStore.country,
		language: languageStore.language,
		pageType: NewPageType.Upcoming,
		first: 32,
	}),
	{ enabled: enabled }
);

const upcomingProvidersData = computed(() => {
	const upcomingReleases =
		result.value?.newTitles?.edges
			?.map(title => {
				return title?.node?.content?.upcomingReleases;
			})
			?.flat() ?? [];

	const providerIds = upcomingReleases.map(provider => provider?.package?.packageId);

	// Return array without duplicates or undefined values
	return [...new Set(providerIds)].filter(Boolean) as number[];
});

onResult(() => {
	constantStore.setUpcomingProviders(upcomingProvidersData.value);
});

const selectedProviders = computed(() =>
	props.userProviders.filter(provider => props.currentFilters.providers.includes(provider.shortName))
);

const plusIconIsVisible = computed(() => {
	if (props.addButtonVisible) {
		return userStore.settings?.providers?.length > 0;
	}
	return props.addButtonVisible;
});

const filteredProviders = computed(() => {
	if (props.selectionType === 'bundle') {
		return props.userProviders.filter(provider => !props.currentFilters.providers.includes(provider.shortName));
	}
	return props.userProviders;
});

const isPricedropsPage = computed(() => {
	return (
		props.collectionType === CollectionType.NEW &&
		routingStore.activeRoute?.meta?.newPageType === NewPageType.PriceDrop
	);
});

const recalculatedProviderUrlProp = computed(() => {
	return `${props.webLocale}_${isPricedropsPage.value ? 'pricedrop' : 'normal'}`;
});

const providerTag = computed(() => (isPricedropsPage.value ? 'button' : 'a'));

watch(
	recalculatedProviderUrlProp,
	async () => {
		const providersByShortNameVal = constantStore.providersByShortName;
		const genresByShortNameVal = constantStore.genresByShortName;
		const subgenresListVal = constantStore.subgenresList;

		if (isPricedropsPage.value) {
			providerUrls.value = {};
		} else {
			providerUrls.value = props.userProviders.reduce((root, provider: Provider) => {
				root[provider.shortName] = calculateUrl(
					router,
					undefined,
					props.collectionType,
					{
						// we only want content_types and providers in the <a href>.
						// no ?genres=crm or other filters.
						content_types: props.currentFilters.content_types,
						providers: [provider.shortName],
					},
					providersByShortNameVal,
					genresByShortNameVal,
					subgenresListVal
				);
				return root;
			}, {} as Record<string, string>);
		}
	},
	{ immediate: true }
);

const isFiltersContainsProvider = (providerName: string) => {
	return props.currentFilters.providers.includes(providerName);
};

const isProviderActive = (providerName: string) => {
	return props.currentFilters.providers.length === 0 || isFiltersContainsProvider(providerName);
};

const isProviderDisabled = (providerId: number) => {
	return props.collectionType === CollectionType.UPCOMING && !upcomingProvidersData.value.includes(providerId);
};

async function providerClicked(providerName: string) {
	if (!props.providersClickable) return;

	const isActive: 1 | 0 = isFiltersContainsProvider(providerName) ? 0 : 1;

	const toggleData = {
		filterName: 'providers',
		value: providerName,
		selectedState: isActive,
		updateRouteState: props.updateRouteState,
		userSelectedPackages: props.userSelectedPackages,
		trackEvent: props.trackEvent,
	} as const;

	const newRoute = await filterStore.toggle(toggleData);

	emit('toggle', toggleData);

	if (newRoute) router.push(newRoute);
}

function onWillDismiss() {
	isModalOpening.value = false;
}

async function openProviderSelection() {
	isModalOpening.value = true;
	await ModalHelper.openModal(
		PackagesSelectionModal,
		{ providerPlace: ProviderFilterPlace.SETTINGS, onWillDismiss },
		{ cssClass: 'packages-selection-modal' },
		ProviderFilterPlace.SETTINGS,
		true
	);
	isModalOpening.value = false;
}

return { __sfc: true,FilterBarProviderMulti, props, emit, router, constantStore, filterStore, languageStore, routingStore, userStore, IMG_PROVIDERS_BAR_LOAD_COUNT, plusIcon, isModalOpening, providerUrls, enabled, result, onResult, upcomingProvidersData, selectedProviders, plusIconIsVisible, filteredProviders, isPricedropsPage, recalculatedProviderUrlProp, providerTag, isFiltersContainsProvider, isProviderActive, isProviderDisabled, providerClicked, onWillDismiss, openProviderSelection, HiddenHorizontalScrollbar, ProviderIconSquare }
}

})